import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { eventsService } from '@/lib/api/services/events'
import type { CreateEvent, UpdateEvent, PaginationParams } from '@/lib/api/types'
import {eventKeys as eventKeysAlias } from '@/lib/api/hooks/event'

// Query keys for events management
export const eventKeys = {
  all: ['events'] as const,
  events: () => [...eventKeys.all, 'events'] as const,
  event: (id: string) => [...eventKeys.events(), id] as const,
  eventsPaginated: (params: PaginationParams) => [...eventKeys.events(), 'paginated', params] as const,
  eventsAll: () => [...eventKeys.events(), 'all'] as const,
  eventsPublic: () => [...eventKeys.events(), 'public'] as const,
  eventsUpcoming: () => [...eventKeys.events(), 'upcoming'] as const,
  eventsOngoing: () => [...eventKeys.events(), 'ongoing'] as const,
  eventsCompleted: () => [...eventKeys.events(), 'completed'] as const,
  eventsSearch: (query: string) => [...eventKeys.events(), 'search', query] as const,
  eventsByCategory: (category: string) => [...eventKeys.events(), 'category', category] as const,
  stats: () => [...eventKeys.all, 'stats'] as const,
  categories: () => [...eventKeys.all, 'categories'] as const,
  registrations: (eventId: string) => [...eventKeys.event(eventId), 'registrations'] as const
}

/**
 * Get events with pagination
 */
export const useEvents = (params: PaginationParams = { page: 1, size: 10 }) => {
  return useQuery({
    queryKey: eventKeys.eventsPaginated(params),
    queryFn: () => eventsService.getEvents(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: (previousData) => previousData
  })
}

/**
 * Get all events (unpaginated)
 */
export const useAllEvents = () => {
  return useQuery({
    queryKey: eventKeys.eventsAll(),
    queryFn: () => eventsService.getAllEvents(),
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

/**
 * Get public events
 */
export const usePublicEvents = () => {
  return useQuery({
    queryKey: eventKeys.eventsPublic(),
    queryFn: () => eventsService.getPublicEvents(),
    staleTime: 10 * 60 * 1000 // 10 minutes
  })
}

/**
 * Get event by ID
 */
export const useEvent = (eventId: string) => {
  return useQuery({
    queryKey: eventKeys.event(eventId),
    queryFn: () => eventsService.getEventById(eventId),
    enabled: !!eventId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

/**
 * Get upcoming events
 */
export const useUpcomingEvents = () => {
  return useQuery({
    queryKey: eventKeys.eventsUpcoming(),
    queryFn: () => eventsService.getUpcomingEvents(),
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

/**
 * Get ongoing events
 */
export const useOngoingEvents = () => {
  return useQuery({
    queryKey: eventKeys.eventsOngoing(),
    queryFn: () => eventsService.getOngoingEvents(),
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}

/**
 * Get completed events
 */
export const useCompletedEvents = () => {
  return useQuery({
    queryKey: eventKeys.eventsCompleted(),
    queryFn: () => eventsService.getCompletedEvents(),
    staleTime: 10 * 60 * 1000 // 10 minutes
  })
}

/**
 * Search events
 */
export const useSearchEvents = (searchQuery: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: eventKeys.eventsSearch(searchQuery),
    queryFn: () => eventsService.searchEvents(searchQuery),
    enabled: enabled && !!searchQuery && searchQuery.length > 2,
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}

/**
 * Get events by category
 */
export const useEventsByCategory = (category: string) => {
  return useQuery({
    queryKey: eventKeys.eventsByCategory(category),
    queryFn: () => eventsService.getEventsByCategory(category),
    enabled: !!category,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

/**
 * Infinite scroll for events
 */
export const useInfiniteEvents = () => {
  return useInfiniteQuery({
    queryKey: [...eventKeys.events(), 'infinite'],
    queryFn: ({ pageParam }: { pageParam: number }) => eventsService.getEvents({ page: pageParam, size: 10 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: any, allPages: any[]) => {
      if (!lastPage.success || !lastPage.data) return undefined

      const currentPage = allPages.length
      const totalPages = lastPage.data.totalPage

      return currentPage < totalPages ? currentPage + 1 : undefined
    },
    staleTime: 2 * 60 * 1000
  })
}

/**
 * Get event statistics
 */
export const useEventStats = () => {
  return useQuery({
    queryKey: eventKeys.stats(),
    queryFn: async () => {
      const [total, upcoming, ongoing, completed] = await Promise.all([
        eventsService.getTotalEvents(),
        eventsService.getUpcomingEvents(),
        eventsService.getOngoingEvents(),
        eventsService.getCompletedEvents()
      ])

      return {
        total: total.data?.count || 0,
        upcoming: upcoming.data?.length || 0,
        ongoing: ongoing.data?.length || 0,
        completed: completed.data?.length || 0
      }
    },
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

/**
 * Get event categories
 */
export const useEventCategories = () => {
  return useQuery({
    queryKey: eventKeys.categories(),
    queryFn: () => eventsService.getEventCategories(),
    staleTime: 30 * 60 * 1000 // 30 minutes
  })
}

/**
 * Get event registrations
 */
export const useEventRegistrations = (eventId: string) => {
  return useQuery({
    queryKey: eventKeys.registrations(eventId),
    queryFn: () => eventsService.getEventRegistrations(eventId),
    enabled: !!eventId,
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}

/**
 * Create event mutation
 */
export const useCreateEvent = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateEvent) => eventsService.createEvent(data),
    onSuccess: () => {
      // Invalidate events list
      queryClient.invalidateQueries({ queryKey: eventKeys.events() })
      queryClient.invalidateQueries({ queryKey: eventKeys.stats() })
      queryClient.invalidateQueries({ queryKey: eventKeysAlias.all })
    },
    onError: (error) => {
      console.error('Event creation failed:', error)
    }
  })
}

/**
 * Update event mutation
 */
export const useUpdateEvent = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: Partial<UpdateEvent> }) =>
      eventsService.updateEvent(eventId, data),
    onSuccess: (response, { eventId }) => {
      // Update specific event in cache
      if (response.success && response.data) {
        queryClient.setQueryData(eventKeys.event(eventId), response.data)
      }

      // Invalidate events list and stats
      queryClient.invalidateQueries({ queryKey: eventKeys.events() })
      queryClient.invalidateQueries({ queryKey: eventKeys.stats() })
      queryClient.invalidateQueries({ queryKey: eventKeysAlias.all })
    },
    onError: (error) => {
      console.error('Event update failed:', error)
    }
  })
}

/**
 * Update event status mutation
 */
export const useUpdateEventStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      eventId,
      status
    }: {
      eventId: string
      status: 'DRAFT' | 'PUBLISH'
    }) => eventsService.updateEventStatus(eventId, status),
    onSuccess: (response, { eventId }) => {
      // Update specific event in cache
      if (response.success && response.data) {
        queryClient.setQueryData(eventKeys.event(eventId), response.data)
      }

      // Invalidate events list and stats
      queryClient.invalidateQueries({ queryKey: eventKeys.events() })
      queryClient.invalidateQueries({ queryKey: eventKeys.stats() })
      queryClient.invalidateQueries({ queryKey: eventKeysAlias.all })
    },
    onError: (error) => {
      console.error('Event status update failed:', error)
    }
  })
}

/**
 * Delete event mutation
 */
export const useDeleteEvent = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (eventId: string) => eventsService.deleteEvent(eventId),
    onSuccess: (_, eventId) => {
      // Remove event from cache
      queryClient.removeQueries({ queryKey: eventKeys.event(eventId) })

      // Invalidate events list and stats
      queryClient.invalidateQueries({ queryKey: eventKeys.events() })
      queryClient.invalidateQueries({ queryKey: eventKeys.stats() })
      queryClient.invalidateQueries({ queryKey: eventKeysAlias.all })
    },
    onError: (error) => {
      console.error('Event deletion failed:', error)
    }
  })
}

/**
 * Upload event image mutation
 */
export const useUploadEventImage = () => {
  return useMutation({
    mutationFn: ({ file, eventId }: { file: File; eventId?: string }) =>
      eventsService.uploadEventImage(file, eventId),
    onError: (error) => {
      console.error('Event image upload failed:', error)
    }
  })
}

/**
 * Register for event mutation
 */
export const useRegisterForEvent = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ eventId, userData }: { eventId: string; userData: any }) =>
      eventsService.registerForEvent(eventId, userData),
    onSuccess: (_, { eventId }) => {
      // Invalidate event and registrations
      queryClient.invalidateQueries({ queryKey: eventKeys.event(eventId) })
      queryClient.invalidateQueries({ queryKey: eventKeys.registrations(eventId) })
    },
    onError: (error) => {
      console.error('Event registration failed:', error)
    }
  })
}